import { zodResolver } from '@hookform/resolvers/zod';
import { CircleCheckBig, CircleX, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CharacterCounter } from '@/components/common/CharacterCounter';
import { WarningNotice } from '@/components/common/WarningNotice';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { getPreferredScrollBehavior } from '@/utils/motion';
import { REVIEW_DECISION, REVIEWER_COMMENT_MAX_LENGTH } from './reviewDecision.constants';
import {
  approveReviewSchema,
  rejectReviewSchema,
  type ReviewDecision,
  type ReviewPayload,
} from './reviewDecision.types';

interface DecisionDisplay {
  title: string;
  placeholder: string;
  variant: 'default' | 'destructive';
  isCommentRequired: boolean;
}

const DECISION_DISPLAY: Record<ReviewDecision, DecisionDisplay> = {
  APPROVE: {
    title: 'Approval comment',
    placeholder: 'Add a note for the ZM',
    variant: 'default',
    isCommentRequired: false,
  },
  REJECT: {
    title: 'Rejection comment',
    placeholder: 'Tell the ZM why it was rejected',
    variant: 'destructive',
    isCommentRequired: true,
  },
};

interface ReviewDecisionActionsProps {
  isPending: boolean;
  onSubmit: (decision: ReviewDecision, payload: ReviewPayload, onDone: () => void) => void;
  approveWarning?: string | undefined;
}

export function ReviewDecisionActions({
  isPending,
  onSubmit,
  approveWarning,
}: ReviewDecisionActionsProps) {
  const [decision, setDecision] = useState<ReviewDecision | null>(null);
  const [isDiscardPending, setIsDiscardPending] = useState(false);
  const decisionSectionRef = useRef<HTMLElement>(null);

  const form = useForm<ReviewPayload>({
    resolver: zodResolver(
      decision === REVIEW_DECISION.REJECT ? rejectReviewSchema : approveReviewSchema,
    ),
    defaultValues: { comment: '' },
  });
  const comment = form.watch('comment');

  useEffect(() => {
    const section = decisionSectionRef.current;
    if (!section) return;
    section.querySelector('textarea')?.focus({ preventScroll: true });
    section.scrollIntoView({ block: 'end', behavior: getPreferredScrollBehavior() });
  }, [decision]);

  function openDecision(next: ReviewDecision) {
    form.reset({ comment: '' });
    setIsDiscardPending(false);
    setDecision(next);
  }

  function closeDecision() {
    if (isPending) return;
    setIsDiscardPending(false);
    setDecision(null);
  }

  function requestClose() {
    if (isPending) return;
    if (comment.trim().length > 0) {
      setIsDiscardPending(true);
      return;
    }
    closeDecision();
  }

  function submitDecision() {
    if (!decision) return;
    void form.handleSubmit((payload) => {
      onSubmit(decision, payload, closeDecision);
    })();
  }

  if (!decision) {
    return (
      <section
        aria-label="Review"
        className="flex flex-wrap items-center justify-end gap-2 max-sm:grid max-sm:grid-cols-2"
      >
        <Button
          type="button"
          variant="destructive"
          disabled={isPending}
          onClick={() => openDecision(REVIEW_DECISION.REJECT)}
          className="max-sm:h-10"
        >
          <CircleX />
          Reject
        </Button>
        <Button
          type="button"
          disabled={isPending}
          onClick={() => openDecision(REVIEW_DECISION.APPROVE)}
          className="max-sm:h-10"
        >
          <CircleCheckBig />
          Approve
        </Button>
      </section>
    );
  }

  const decisionDisplay = DECISION_DISPLAY[decision];
  const warning = decision === REVIEW_DECISION.APPROVE ? approveWarning : undefined;

  return (
    <section
      ref={decisionSectionRef}
      aria-label={decisionDisplay.title}
      className="flex scroll-mb-4 flex-col gap-3 rounded-xl border bg-card p-4 animate-in fade-in-0 slide-in-from-bottom-2 animation-duration-300 ease-out motion-reduce:animate-none"
    >
      <div className="flex items-center gap-2 px-0.5">
        <h3 className="text-sm font-medium">{decisionDisplay.title}</h3>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-2xs font-medium',
            decisionDisplay.isCommentRequired
              ? 'bg-destructive/10 text-destructive'
              : 'bg-muted text-muted-foreground',
          )}
        >
          {decisionDisplay.isCommentRequired ? 'Required' : 'Optional'}
        </span>
      </div>

      {warning && <WarningNotice>{warning}</WarningNotice>}

      <Form {...form}>
        <FormField
          control={form.control}
          name="comment"
          render={({ field }) => (
            <FormItem className="gap-1.5">
              <FormLabel className="sr-only">Comment</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  maxLength={REVIEWER_COMMENT_MAX_LENGTH}
                  placeholder={decisionDisplay.placeholder}
                  disabled={isPending}
                  className="field-sizing-fixed min-h-32 resize-none overflow-y-auto rounded-lg border-transparent bg-muted/60 focus-visible:border-ring focus-visible:bg-transparent"
                />
              </FormControl>
              <FormMessage className="px-0.5" />
            </FormItem>
          )}
        />
      </Form>

      <div className="flex min-h-8 items-center justify-between gap-2">
        {isDiscardPending ? (
          <>
            <p className="px-0.5 text-xs text-muted-foreground">Discard your comment?</p>
            <div className="flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsDiscardPending(false)}
              >
                Keep
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={closeDecision}
                className="text-destructive hover:text-destructive"
              >
                Discard
              </Button>
            </div>
          </>
        ) : (
          <>
            <CharacterCounter
              current={comment.length}
              max={REVIEWER_COMMENT_MAX_LENGTH}
              className="px-0.5"
            />
            <div className="flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={requestClose}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                variant={decisionDisplay.variant}
                onClick={submitDecision}
                disabled={isPending}
              >
                {isPending && <Loader2 className="animate-spin" aria-hidden />}
                {isPending ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
